import React, { useEffect } from 'react';
import Footer from '../components/Footer';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getDomain } from '../utils/getDomain';
import {
  BILLING_DESCRIPTOR,
  brandNameFromDomain,
  COMPANY_ADDRESS,
  COMPANY_ADDRESS_KIND,
  COMPANY_ENTITY_TYPE,
  COMPANY_GOVERNING_LAW,
  COMPANY_JURISDICTION,
  COMPANY_NAME,
  COMPANY_UBI,
  COMPANY_VENUE,
  companyDbaLine,
} from '../constants/company';

const pStyle = {
  boxSizing: 'inherit',
  margin: '0px 0px 1.8em',
};
const hStyle = {
  boxSizing: 'inherit',
  margin: '0px 0px 0.4em',
  fontWeight: 700,
};

function TermsOfService() {
  const domain = getDomain();
  const brandName = brandNameFromDomain(domain);
  const contactEmail = `contact@${domain}`;

  useDocumentTitle('Terms of Service');
  useScripts();

  useEffect(() => {
    document.body.classList.add('login-form');
    return () => {
      document.body.classList.remove('login-form');
    };
  }, []);

  useEffect(() => {
    const triggerAnimations = () => {
      window.dispatchEvent(new Event('scroll'));
      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.animated');
        animatedElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible) {
            const originalClasses = el.className;
            el.className = originalClasses.replace(/\banimated\b/g, '').trim();
            void el.offsetHeight;
            setTimeout(() => {
              el.className = originalClasses;
            }, 10);
          }
        });
      }, 100);
    };

    triggerAnimations();
    setTimeout(triggerAnimations, 200);
    setTimeout(triggerAnimations, 500);
  }, []);

  return (
    <>
      <section className="bg-form animatedParent" style={{ paddingTop: '60px', paddingBottom: '60px' }}>
        <div className="container">
          <h2 className="text-center" style={{ marginBottom: '40px' }}>Terms of Service</h2>
          <div style={{
            boxSizing: 'inherit',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '92%',
            width: '740px',
            fontSize: '20px',
            lineHeight: '1.8',
            color: 'rgb(34, 34, 34)',
            fontFamily: 'Arimo, sans-serif'
          }}>
            <p style={pStyle}>
              This is an agreement between you and {companyDbaLine(domain)} at {domain}
              for virtual private network communications services, related services and features.
              Last updated: October 5, 2026. {COMPANY_NAME} is {COMPANY_ENTITY_TYPE}.
              The contracting and registered office is {COMPANY_ADDRESS}, in {COMPANY_JURISDICTION}.
              {COMPANY_UBI ? ` Washington UBI ${COMPANY_UBI}.` : ''}
              This is not a service for criminal activity. You agree not to violate the laws that apply to you
              in any place you connect from or to.
            </p>

            <p style={pStyle}>
              You agree to protect your user id / password and our system from unauthorized use. You are responsible
              for all actions that occur on our services where the login uses your user id / password.
            </p>

            <p style={pStyle}>
              You agree not to use the system for sending spam, port scanning, scanning for open proxies or open relays,
              sending opt-in email, unsolicited email or any type or version of email sent in large quantities even if
              the email is ultimately sent off of another server. You agree not to launch any pop-ups from our service.
              You agree not to attack in any way shape or form any other computer or network while on our service.
            </p>

            <p style={pStyle}>
              Actual service coverage, speeds, locations, and quality may vary. The service will attempt to be available
              at all times except for limited periods for maintenance and repair. However, the Service may be subject to
              unavailability for a variety of factors beyond our control including emergencies, third party service failures,
              transmission, equipment or network problems or limitations, interference, signal strength, and may be interrupted,
              refused, limited or curtailed. We are not responsible for data, messages or pages lost, not delivered, delayed
              or misdirected because of interruptions or performance issues with the Service or communications services or
              networks (e.g., T-1 lines or the Internet). We may impose usage or Service limits, suspend Service, or block
              certain kinds of usage in our sole discretion to protect users or the Service. Network speed is an estimate and
              is no indication of the speed at which your or the Service sends or receives data. Actual network speed will vary
              based on configuration, compression, network congestion and other factors. The accuracy and timeliness of data
              received is not guaranteed; delays or omissions may occur.
            </p>

            <p style={pStyle}>
              VPN traffic is not logged: we do not record sites visited, DNS lookups, or the content of VPN sessions.
              Website and account operations are different. We may keep website access logs, application error logs,
              account records, and payment records as described in our Privacy Policy, including cookies.
              We do not sell your personal information. We share information with processors (such as payment, email, and hosting)
              as needed to run the service. We do not cooperate with requests for information unless
              ordered by a court of competent jurisdiction.
            </p>

            <p style={hStyle}>PLANS AND BILLING</p>
            <p style={pStyle}>
              {brandName} sells prepaid, one-time purchases of time-limited VPN access (for example 1 day,
              30 days, 90 days, or 365 days). These are not free trials, memberships, or automatically renewing subscriptions.
              You are charged once at checkout for the selected period. The plan does not auto-renew, and we do not bill you
              again unless you make a new purchase. Access ends when the purchased period expires. References on the site to
              an “active plan” mean paid access that has not yet expired.
            </p>

            <p style={hStyle}>DELIVERY</p>
            <p style={pStyle}>
              This is a digital product. No physical goods are shipped. After a successful payment we provide VPN credentials
              and setup instructions by email and on the payment confirmation page. You are responsible for downloading the
              profile or app and completing setup. Delivery is complete when credentials or download access have been made
              available to the email address you provided at checkout.
            </p>

            <p style={hStyle}>BILLING DESCRIPTOR</p>
            <p style={pStyle}>
              Charges are processed by our payment provider. Your card statement may show this purchase as {BILLING_DESCRIPTOR}
              or a similar billing descriptor. If you do not recognize a charge, contact {contactEmail} with the date and amount
              before disputing it with your bank.
            </p>

            <p style={hStyle}>VPN CLIENT SOFTWARE</p>
            <p style={pStyle}>
              We provide VPN access, credentials, and configuration profiles. You connect using compatible
              third-party VPN client software on your devices. Those applications are published, licensed,
              and updated by their own developers. We do not own or publish the client software you install,
              and we are not those publishers. We support delivery of your credentials and your {brandName}
              account. Support for the third-party apps themselves is provided by their publishers.
              You are responsible for installing an official build from the publisher’s site or store and for
              keeping that software updated.
            </p>

            <p style={hStyle}>CANCELLATION</p>
            <p style={pStyle}>
              Because this is a prepaid one-time purchase, there is no recurring plan to cancel and no future charges
              to stop. Unused time remaining in a paid period is not refunded if you stop using the service before the period ends.
            </p>

            <p style={hStyle}>REFUNDS</p>
            <p style={pStyle}>
              By completing checkout, or by accepting, downloading, installing, or otherwise accessing the software or credentials,
              you acknowledge that your purchase is final and NON-REFUNDABLE except where required by applicable law.
              Once digital access has been delivered, no refunds, credits, or cancellations will be permitted except where
              that law requires otherwise.
            </p>

            <p style={hStyle}>GOVERNING LAW</p>
            <p style={pStyle}>
              These terms are governed by {COMPANY_GOVERNING_LAW}.
              You and {COMPANY_NAME} agree that exclusive venue for any dispute arising out of these terms or the service
              is {COMPANY_VENUE}, except where applicable law requires otherwise.
            </p>

            <p style={hStyle}>SUPPORT</p>
            <p style={pStyle}>
              For billing, access, or setup questions, email {contactEmail} or use the Contact page. We aim to respond within
              two business days. {COMPANY_NAME}, d/b/a {brandName}, {COMPANY_ADDRESS_KIND}: {COMPANY_ADDRESS}.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default TermsOfService;
